import { Dialog, Stack, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import { DialogTitleClosable } from "../../../components/DialogTitleClosable";
import { bhpButtonSx } from "../../../styles/bhpStyles";
import { CardSwapSearchResultDto } from "../dtos/CardSwapSearchResultDto";
import { useContext, useState } from "react";
import { CardSwapUpdateDto } from "../dtos/CardSwapUpdateDto";
import VspPortalContext from "../../../store/VspPortalContext";
import { useCardSwapHttp } from "../hooks/useCardSwapHttp";
import { LoadingButton } from "@mui/lab";
import { CardSwapStatusCode, cardSwapStatusCodes } from "../enums/CardSwapStatusCode";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import UpdateIcon from '@mui/icons-material/Update';
import CancelIcon from '@mui/icons-material/Cancel';
import { useCardSwap } from "../hooks/useCardSwap";


type CreatingNewCardDialogProps = {

    cardSwapSearchResultDto : CardSwapSearchResultDto;
    dialogOpen: boolean;
    onClose: ( isUpdated : boolean ) => void;

};


export const CreatingNewCardDialog : React.FC< CreatingNewCardDialogProps > = ( props : CreatingNewCardDialogProps ) => {

    const { cardSwapSearchResultDto, dialogOpen, onClose } = props;

    const [ cardNumber, setCardNumber ] = useState< string >( '' );
    const [ isCardNumberValid, setIsCardNumberValid ] = useState< boolean >( false );
    const [ isUpdating, setIsUpdating ] = useState< boolean >( false );

    const { state } = useContext( VspPortalContext );

    const { updateCardSwap } = useCardSwapHttp( );

    const { cardSwapSearchResult } = useCardSwap( cardSwapSearchResultDto );


    const checkCardNumberValidity = ( cardNumber : string ) => {

        const containsNumbersOnly = /^\d+$/.test( cardNumber );

        if( containsNumbersOnly && cardNumber.length >= 6 && cardNumber.length <= 8 ){

            setIsCardNumberValid( true );

        } else {

            setIsCardNumberValid( false );

        }

    };

    
    const handleCardNumberChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {

        const value = event.target.value;
        setCardNumber( value );
        checkCardNumberValidity( value );

    };

    
    const handleCardNumberKeyUp = ( event: React.KeyboardEvent<HTMLDivElement> ) => {

        if( event.key !== 'Enter' || !isCardNumberValid ) {
            
            return;

        }

        update( );

    };

    
    const handleClose = ( ) => {

        if( !isUpdating ) {
            
            onClose( false );

        }

    }

    
    const handleOkClick = ( ) => update( );


    const update = async( ) => {

        const cardSwapUpdateDto : CardSwapUpdateDto = {

            cardSwapStatusCode : CardSwapStatusCode.NewCardCreated,
            id : cardSwapSearchResult.id,
            newCardNumber : cardNumber,
            updatedBy : state.username

        };

        setIsUpdating( true );
        const isSuccess = await updateCardSwap( cardSwapUpdateDto ); 
        setIsUpdating( false );


        if( isSuccess ){
            
            onClose( true );

        }

    };


    return(

        <Dialog
            onClose={ handleClose }
            open={ dialogOpen }
            sx={{ '& .MuiDialog-paper': { maxHeight: 435, width: '500px' } }}
        >
            <DialogTitleClosable onClose={ handleClose }>
                <Stack direction="row" display="flex" alignItems="center" gap={ 0.5 }>
                    <CreditCardIcon /> 
                    { cardSwapStatusCodes[ cardSwapSearchResultDto.refCardSwapStatusCode ] }
                </Stack>
            </DialogTitleClosable>

            <DialogContent dividers>
                
                <ul>

                    <li><b>First Name:</b> { cardSwapSearchResult.firstName }</li>
                    <li><b>Last Name:</b> { cardSwapSearchResult.surname }</li>
                    <li><b>Email:</b> { cardSwapSearchResult.email }</li>
                    <li><b>Employee / ERMS ID:</b> { cardSwapSearchResult.employeeNumber }</li>
                    <li><b>Card Collection Village:</b> { cardSwapSearchResult.villageCode }</li>

                </ul>

                <TextField 
                    sx={{ width : '100%' }} 
                    label="Card Number" 
                    variant="outlined"
                    value={ cardNumber }
                    onChange={ handleCardNumberChange }
                    onKeyUp={ handleCardNumberKeyUp }
                    disabled={ isUpdating }
                />
                
                { 
                    ( !isCardNumberValid && cardNumber )
                        ?
                    <span style={{ color : 'red', fontSize : '12px' }}>
                        * Card number should contain numbers only from 6 to 8 digits
                    </span>
                        :
                    <span style={{ fontSize : '12px' }}>
                        &nbsp;
                    </span>

                }


            </DialogContent>
            
            <DialogActions>

                <LoadingButton
                    disabled={ !isCardNumberValid || isUpdating }
                    loading={ isUpdating }
                    loadingPosition="start"
                    onClick={ handleOkClick } 
                    startIcon={ <UpdateIcon /> }
                    sx={{ ...bhpButtonSx, width: '100px' }} 
                    variant="contained"
                >
                    Ok
                </LoadingButton>

                <Button 
                    disabled={ isUpdating }
                    onClick={ handleClose } 
                    startIcon={ <CancelIcon /> }
                    sx={{ ...bhpButtonSx, width: '100px' }} 
                >
                    Close
                </Button>
            </DialogActions>

        </Dialog>

    );


};